import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { AccountSettings, BankAccount } from '../types'
import { toISODate } from '../lib/dateRules'

export interface AccountFormValues {
  name: string
  includeInTotal: boolean
  settings: AccountSettings
}

interface Props {
  /** Absent when adding a new account. */
  initial?: BankAccount
  /** Only passed when deleting is allowed — never for the last account. */
  onDelete?: () => void
  onSave: (values: AccountFormValues) => void
  onClose: () => void
}

/** One account's name, starting balance and thresholds. */
export default function AccountForm({ initial, onDelete, onSave, onClose }: Props) {
  const [name, setName] = useState(initial?.name ?? '')
  const [includeInTotal, setIncludeInTotal] = useState(initial?.includeInTotal ?? true)
  const [startingBalance, setStartingBalance] = useState(initial?.settings.startingBalance.toString() ?? '0')
  const [startingBalanceDate, setStartingBalanceDate] = useState(
    initial?.settings.startingBalanceDate ?? toISODate(new Date()),
  )
  const [bad, setBad] = useState(initial?.settings.ragThresholds.bad.toString() ?? '0')
  const [medium, setMedium] = useState(initial?.settings.ragThresholds.medium.toString() ?? '300')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      name: name.trim() || 'Untitled account',
      includeInTotal,
      settings: {
        startingBalance: parseFloat(startingBalance) || 0,
        startingBalanceDate,
        ragThresholds: { bad: parseFloat(bad) || 0, medium: parseFloat(medium) || 0 },
      },
    })
  }

  return (
    <Modal title={initial ? 'Edit account' : 'Add account'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Name">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={!initial}
            placeholder="e.g. Joint account, Savings"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Starting balance">
            <input className={inputClass} type="number" step="0.01" value={startingBalance} onChange={(e) => setStartingBalance(e.target.value)} />
          </Field>
          <Field label="As of date">
            <input className={inputClass} type="date" value={startingBalanceDate} onChange={(e) => setStartingBalanceDate(e.target.value)} required />
          </Field>
        </div>

        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeInTotal}
            onChange={(e) => setIncludeInTotal(e.target.checked)}
          />
          <span className="text-xs font-sans text-ink/80">
            Count this account in the combined balance
            <span className="block text-[11px] text-ink/70 mt-0.5">
              Untick for money that isn't really spendable — a pension, or savings held for someone else.
            </span>
          </span>
        </label>

        <div className="border-t border-line pt-3 mt-1">
          <p className="text-xs text-ink/75 mb-3">
            RAG thresholds for this account. Balances at or below "bad" are red; at or below "tight" (and above bad) are
            amber; anything higher is green.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Bad threshold (≤)">
              <input className={inputClass} type="number" step="0.01" value={bad} onChange={(e) => setBad(e.target.value)} />
            </Field>
            <Field label="Tight threshold (≤)">
              <input className={inputClass} type="number" step="0.01" value={medium} onChange={(e) => setMedium(e.target.value)} />
            </Field>
          </div>
        </div>

        {confirmingDelete && onDelete && (
          <div className="rounded-sm bg-rag-badBg px-3 py-2 mb-3 text-sm text-rag-bad">
            <p>
              Delete <span className="font-medium">{initial?.name}</span> and everything in it? Its income, outgoings,
              budgets and one-offs go with it.
            </p>
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={onDelete} className="font-medium hover:underline">
                Yes, delete it
              </button>
              <button type="button" onClick={() => setConfirmingDelete(false)} className="text-ink/75 hover:underline">
                Keep it
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-line">
          <div>
            {onDelete && !confirmingDelete && (
              <button type="button" onClick={() => setConfirmingDelete(true)} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            <button type="submit" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              {initial ? 'Save' : 'Add account'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
